// frontend/src/features/equipment/EquipmentCard.tsx

import {
  Card,
  Text,
  Group,
  Badge,
  ActionIcon,
  Menu,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconArchive,
} from '@tabler/icons-react';
import type { Equipment } from '@/types/equipment';
import { CATEGORY_LABELS } from '@/types/equipment';

interface EquipmentCardProps {
  equipment: Equipment;
  onEdit: (equipment: Equipment) => void;
  onRetire: (equipment: Equipment) => void;
  onDelete: (equipmentId: string) => void;
}

export function EquipmentCard({
  equipment,
  onEdit,
  onRetire,
  onDelete,
}: EquipmentCardProps) {
  return (
    <Card withBorder style={{ cursor: 'pointer' }} onClick={() => onEdit(equipment)}>
      <Group justify="space-between" mb="xs">
        <div>
          <Text fw={500}>{equipment.name}</Text>
          <Text size="sm" c="dimmed">
            {equipment.brand} {equipment.model}
          </Text>
        </div>
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onEdit(equipment);
              }}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={<IconArchive size={14} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onRetire(equipment);
              }}
            >
              {equipment.status === 'active' ? 'Retire' : 'Reactivate'}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDelete(equipment.id);
              }}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Group gap="xs" mb="xs">
        <Badge variant="light">
          {CATEGORY_LABELS[equipment.category] || equipment.category}
        </Badge>
        {equipment.subcategory && (
          <Badge variant="outline" size="sm">
            {equipment.subcategory}
          </Badge>
        )}
        {equipment.status === 'retired' && (
          <Badge color="gray">Retired</Badge>
        )}
      </Group>

      {equipment.install_date && (
        <Text size="xs" c="dimmed">
          Installed: {new Date(equipment.install_date).toLocaleDateString()}
        </Text>
      )}
    </Card>
  );
}
